import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import { fi } from 'date-fns/locale';
import { IconButton, Stack } from '@mui/material';

import DeleteIcon from 'shared/icons/Delete';
import EditIcon from 'shared/icons/Edit';
import safeParseHtml from 'shared/utils/safeParseHtml';

import type { TChatMessage } from '../model/types';

import { Edited, Message, MessageAuthor, MessageCreationDate } from './styled';

const formatCreationDate = (createdOn: string | null) => {
  if (!createdOn) return null;

  return format(new Date(createdOn), 'd. MMM yyyy, H:mm', {
    locale: fi,
  });
};

const formatEditDate = (editedOn: string | null) => {
  if (!editedOn) return null;

  return format(new Date(editedOn), 'd.MM.yyyy H:mm');
};

type ChatMessageProps = {
  canBeModified: boolean;
  chatMessage: TChatMessage;
  isActive: boolean;
  onClickDeleteButton?: (chatMessage: TChatMessage) => void;
  onClickEditButton?: (chatMessage: TChatMessage) => void;
};

const ChatMessage = forwardRef<HTMLDivElement, ChatMessageProps>(
  (props, ref) => {
    const {
      canBeModified,
      chatMessage,
      isActive,
      onClickDeleteButton,
      onClickEditButton,
    } = props;

    const { t } = useTranslation();
    const showActionButtons =
      canBeModified &&
      !isActive &&
      chatMessage.IsMyMessage &&
      !chatMessage.IsDeleted;

    return (
      <Stack
        spacing={0.6}
        ref={ref}
        sx={{ padding: { xs: '15px 0px', sm: '15px' } }}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          spacing={1}
        >
          <MessageAuthor>{chatMessage.UserRef.Name}</MessageAuthor>
          <Stack alignItems="center" direction="row" spacing={1}>
            <MessageCreationDate>
              {formatCreationDate(chatMessage.CreatedOn)}
            </MessageCreationDate>

            {showActionButtons && (
              <>
                {onClickEditButton && (
                  <IconButton onClick={() => onClickEditButton(chatMessage)}>
                    <EditIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                )}

                {onClickDeleteButton && (
                  <IconButton onClick={() => onClickDeleteButton(chatMessage)}>
                    <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                )}
              </>
            )}
          </Stack>
        </Stack>
        <Message isDeleted={chatMessage.IsDeleted} isRead={chatMessage.IsRead}>
          {chatMessage.IsDeleted ? (
            t('MessageDeleted')
          ) : (
            <>
              {safeParseHtml(chatMessage.Message)}
              {chatMessage.IsEdited && (
                <>
                  <br />
                  <br />
                  <Edited>
                    {t('MessageEdited')} {formatEditDate(chatMessage.EditedOn)}
                  </Edited>
                </>
              )}
            </>
          )}
        </Message>
      </Stack>
    );
  }
);

export default ChatMessage;
